<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null || allSites"
          @click="showCreateForm()"
        >
          Create New NC
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" :disabled="allSites" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                :disabled="allSites"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field v-if="canViewAllSites" label="All Sites">
              <b-switch v-model="allSites" @input="resetData()">
                Every site
              </b-switch>
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || (!allSites && selectedPlant === null)"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="nc.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="nc"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingNC" scroll="keep" has-modal-card>
      <form v-if="isCreatingNC" @submit.prevent="submitNC">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Create NC
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="newNC.date"
                  name="newdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="Client">
              <b-input v-model="newNC.clientName" type="text" />
            </b-field>
            <b-field label="Comment">
              <b-input v-model="newNC.comment" type="text" />
            </b-field>
            <b-field label="Quantity">
              <b-input v-model="newNC.quantity" type="number" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
    <b-modal :active.sync="isEditingNC" scroll="keep" has-modal-card>
      <form v-if="isEditingNC" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit NC
              <span v-if="allSites" style="font-size:1rem; text-overflow: elipsis">{{ editableNC.plantName }}</span>
              <span v-else-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableNC.date"
                  name="editdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="Client">
              <b-input v-model="editableNC.clientName" type="text" />
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableNC.comment" type="text" />
            </b-field>
            <b-field label="Quantity">
              <b-input v-model="editableNC.quantity" type="number" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import NCDeclaration from '../../services/v3/ncDeclaration'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'NCDeclaration',
  components: {
  },
  mixins: [plantSelector],
  data: function () {
    return {
      editableNC: null,
      newNC: null,
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      start: null,
      end: new Date(),
      isCreatingNC: false,
      isEditingNC: false,
      allSites: false,
      nc: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    canViewAllSites () {
      const rights = this.user?.role?.rights?.map(e => e.name) || []
      return rights.includes('Production Bonus Management')
    },
    // Site names are only worth a column when rows can come from more than one site.
    columnDefs () {
      const plantColumn = {
        headerName: 'Plant',
        field: 'plantName',
        filter: 'agTextColumnFilter',
        width: 220,
        sortable: true,
        filterParams: { suppressAndOrCondition: true }
      }
      const baseColumns = [
        { headerName: 'Date',
          field: 'date',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Client',
          field: 'clientName',
          filter: 'agTextColumnFilter',
          width: 220,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Comment',
          field: 'comment',
          filter: 'agTextColumnFilter',
          width: 400,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Quantity',
          field: 'quantity',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        }
      ]
      return this.allSites ? [plantColumn, ...baseColumns] : baseColumns
    }
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 30)
    // All-sites mode names every plant in the grid, so it needs the full list
    // even when the group is scoped to its own sites.
    await this.loadPlants({ force: this.canViewAllSites })
  },
  methods: {
    resetData () {
      this.nc = []
    },
    showCreateForm () {
      this.isCreatingNC = true
      this.newNC = {
        date: new Date(this.start.toLocaleDateString('en-CA')),
        clientName: '',
        comment: null,
        siteId: this.selectedPlant.id
      }
    },
    generateUserId () {
      this.newNC.matricule = uuidv4()
    },
    showEditForm (nc) {
      this.isEditingNC = true
      this.editableNC = nc
    },
    async getData () {
      if (!this.allSites && (!this.selectedPlant || !this.selectedPlant.id)) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      if (!this.allSites) {
        sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      }
      this.isLoading = true
      try {
        const data = this.allSites
          ? await NCDeclaration.getAllTable({
            start: this.start.toLocaleDateString('en-CA'),
            end: this.end.toLocaleDateString('en-CA')
          })
          : await NCDeclaration.getTable({
            siteId: this.selectedPlant.id,
            start: this.start.toLocaleDateString('en-CA'),
            end: this.end.toLocaleDateString('en-CA')
          })
        data.forEach(element => {
          this.nc.push({
            id: element.id,
            id_site: element.id_site,
            plantName: this.plantNamesById[String(element.id_site)] || element.id_site,
            clientName: element.client_name || '',
            comment: element.comment,
            date: element.date ? new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA') : null,
            quantity: element.quantity
          })
        })
        if (this.nc.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding NC found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedNC = {
        clientName: params.node.data.clientName,
        comment: params.node.data.comment,
        date: new Date(new Date(params.node.data.date).toISOString().split('T')[0] + 'T00:00:00'),
        id: params.node.data.id,
        id_site: params.node.data.id_site,
        plantName: params.node.data.plantName,
        quantity: params.node.data.quantity
      }
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit NC',
        action: () => {
          this.showEditForm(selectedNC)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete NC',
        action: () => {
          this.deleteNC(selectedNC)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    submitNC () {
      this.isCreatingNC = false
      try {
        NCDeclaration.createNC(this.newNC).then(() => {
          this.$buefy.toast.open({ message: 'NC Created', duration: 3000, type: 'is-success' })
          this.resetData()
          this.getData()
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async submitEdit () {
      this.isEditingNC = false
      try {
        await NCDeclaration.updateNC({
          id: this.editableNC.id,
          id_site: this.editableNC.id_site,
          clientName: this.editableNC.clientName,
          comment: this.editableNC.comment,
          date: this.editableNC.date.toLocaleDateString('en-CA'),
          quantity: this.editableNC.quantity
        })
        this.$buefy.toast.open({ message: 'NC Updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteNC (nc) {
      try {
        await NCDeclaration.archiveNC(nc.id)
        this.$buefy.toast.open({ message: 'NC Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
